import { Injectable } from '@angular/core';
import { TestControl, TestCategory, TestRegistry } from '../models/test-control.interface';

/**
 * Service for managing test registrations in the test bench
 */
@Injectable({
  providedIn: 'root'
})
export class TestRegistryService implements TestRegistry {
  private tests = new Map<string, TestControl>();
  private categories = new Map<string, TestCategory>();

  /**
   * Register a new test control
   */
  registerTest(test: TestControl): void {
    if (this.tests.has(test.id)) {
      console.warn(`Test with ID '${test.id}' is already registered. Overwriting.`);
    }
    
    this.tests.set(test.id, test);
  }

  /**
   * Register a new test category
   */
  registerCategory(category: TestCategory): void {
    if (this.categories.has(category.id)) {
      console.warn(`Category with ID '${category.id}' is already registered. Overwriting.`);
    }
    
    this.categories.set(category.id, category);
  }

  /**
   * Get all tests for a specific category, sorted by title
   */
  getTestsByCategory(categoryId: string): TestControl[] {
    return Array.from(this.tests.values())
      .filter(test => test.category === categoryId)
      .sort((a, b) => a.title.localeCompare(b.title));
  }

  /**
   * Get all registered categories, sorted by order
   */
  getAllCategories(): TestCategory[] {
    return Array.from(this.categories.values())
      .sort((a, b) => a.order - b.order);
  }

  /**
   * Unregister a test
   */
  unregisterTest(testId: string): void {
    this.tests.delete(testId);
  }

  /**
   * Check if a test is registered
   */
  hasTest(testId: string): boolean {
    return this.tests.has(testId);
  }

  /**
   * Get a specific test by ID
   */
  getTest(testId: string): TestControl | undefined {
    return this.tests.get(testId);
  }

  /**
   * Get all registered tests
   */
  getAllTests(): TestControl[] {
    return Array.from(this.tests.values());
  }

  /**
   * Get tests count by category
   */
  getTestsCountByCategory(categoryId: string): number {
    return this.getTestsByCategory(categoryId).length;
  }

  /**
   * Clear all tests (useful for testing)
   */
  clearTests(): void {
    this.tests.clear();
  }

  /**
   * Clear all categories (useful for testing)
   */
  clearCategories(): void {
    this.categories.clear();
  }
}